import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { ANALYTICS } from '../../analytics/constants';
import { PayloadProps, track } from '../../analytics/index';
import { EyeIcon, PaymentLinksIcon } from '../../assets/svg';
import { TicketIcon } from '../../assets/svg/TicketIcon';
import { DateAndTime } from '../../components/date-and-time';
import { DateFilter } from '../../components/table/components/date-filter';
import { ViewWrapper } from '../../components/view-wrapper';
import { CONSTANTS } from '../../config/constants';
import { useFloatingAlert } from '../../hooks/useFloatingAlert';
import { useMerchant } from '../../hooks/useMerchant';
import { useScreenSize } from '../../hooks/useScreenSize';
import { Payment } from '../../models/common-payments';
import { ColumnInterface, Filters } from '../../models/table';
import { PaymentLinksCustomServices } from '../../services/payment-links-custom';
import { validatePermission } from '../../utils/common';
import { defaultFilters, defaultPagination } from '../../utils/defaults';
import { TimeZone, TimeZoneContainer } from '../home/components/header/styles';
import { ResumeSection } from '../home/components/resume-section';
import { ResumeContainer } from '../home/styles';
import { DatePlPicker } from './components/date-pl-picker';
import { PaymentLinkCustomHeader } from './components/header';
import { ContainerActionsLinksComponent } from './components/links-table/ContainerActionsComponent';
import { ModalPayment } from './components/modal-payment';
import { ModalPaymentProps } from './components/modal-payment/types';
import { NewLinkPL } from './components/new-link';
import { StatusCustomPaymentLink } from './components/status';
import { PaymentLinkTable } from './components/table';
import { AmountContainer, NoWrapContainer } from './components/table/styles';
import { ContainerActions, SimpleButton, SkeletonSVG } from './pages/links/styles';
import {
	ButtonsContainer,
	HeadContainer,
	NewPaymentLinkButton,
	TableFilterHeader,
	TitlePage,
} from './styles';

type PaymentLink = 'single' | 'batch';

interface ModalPaymentLink {
	show: boolean;
	type?: PaymentLink;
}

const defaultModalPaymentLink: ModalPaymentLink = {
	show: false,
	type: undefined,
};

const defaultModalPayment: ModalPaymentProps = {
	show: false,
	onClose: () => {
		console.log('close');
	},
	data: {} as Payment,
};

const PaymentLinks3 = () => {
	const { width } = useScreenSize();
	const { merchantSelected, getFlowBankorStp } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const flowBanksOrStp = getFlowBankorStp();
	const [filters, setFilters] = useState<Filters>({
		stateSelected: '',
		typeSelected: '',
	});
	const [pagination, setPagination] = useState(defaultPagination);
	const [modal, setModal] = useState(defaultModalPaymentLink);
	const [modalPayment, setModalPayment] = useState(defaultModalPayment);

	const [currentDate, setCurrentDate] = useState<Date>(new Date());

	const { data: statistics, isFetching: isFetcingStatistics } = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getStatistics,
			merchantSelected?.code,
			currentDate,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getStatistics(merchantSelected?.code as string, currentDate),
	});

	const { data: graphs, isLoading: isLoadingGraph } = useQuery({
		enabled: !!merchantSelected && width > 576,
		useErrorBoundary: true,
		queryKey: [CONSTANTS.services.paymentLinksCustom.getGraphData, merchantSelected?.code],
		queryFn: () => PaymentLinksCustomServices.getGraphicsData(merchantSelected?.code as string),
	});

	const {
		data: table,
		isFetching,
		isFetched,
		isLoading: isLoadingTable,
	} = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [CONSTANTS.services.paymentLinksCustom.getLastPayments, merchantSelected?.code],
		queryFn: () => PaymentLinksCustomServices.getLastPayments(merchantSelected?.code as string),
	});

	useEffect(() => {
		setFilters(defaultFilters);
		setPagination(defaultPagination);
	}, [merchantSelected]);

	const handleShowModal = (type: PaymentLink) => {
		setModal({ show: true, type });
	};

	const onClose = () => {
		setModal(defaultModalPaymentLink);
	};

	const handleShowPaymentModal = (payment: Payment) => {
		setModalPayment({ ...modalPayment, show: true, data: payment });
	};

	const onClosePaymentModal = () => {
		setModalPayment(defaultModalPayment);
	};

	const handleDownloadVoucher = (rowData: Payment) => {
		if (rowData.urlCEP) window.open(rowData.urlCEP, '_blank');
		else if (rowData.validationMailPdf) {
			window.open(rowData.validationMailPdf, '_blank');
		} else {
			handleShowAlert({
				message: CONSTANTS.alertMessages.noVoucherAvailable,
				type: 'error',
			});
		}
	};

	const getColumns = () => {
		const columns: ColumnInterface<Payment>[] = [
			{
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				align: 'left',
				cellRender: ({ validatedatDate, validatedatTime }) => (
					<DateAndTime date={validatedatDate} time={validatedatTime} />
				),
			},
			{
				title: 'Monto',
				align: 'left',
				propertyName: 'amount',
				cellRender: ({ amount }) => <AmountContainer>{amount}</AmountContainer>,
			},
			{
				title: 'RUT Pagador',
				propertyName: 'rut',
				align: 'left',
				cellRender: ({ rut }) => <NoWrapContainer>{rut}</NoWrapContainer>,
			},
			{
				title: 'Banco',
				propertyName: 'bankName',
				align: 'left',
				cellRender: ({ bankName }) => (
					<>{bankName === 'DESCONOCIDO' && merchantSelected?.country === 'MX' ? null : bankName}</>
				),
			},
			{
				title: 'Correo electrónico',
				propertyName: 'email',
				align: 'left',
				cellRender: ({ email }) => <>{email}</>,
			},
			{
				title: 'Estatus',
				align: 'left',
				propertyName: 'status',
				cellRender: ({ status }) => (
					<AmountContainer>
						<StatusCustomPaymentLink status={status} />
					</AmountContainer>
				),
			},
			{
				title: 'Nombre de enlace usado',
				propertyName: 'link_payment',
				align: 'left',
				cellRender: ({ presession }) => <>{presession.name}</>,
			},
			{
				title: '',
				propertyName: 'modalPayment',
				align: 'left',
				cellRender: (rowData: Payment) => (
					<ContainerActionsLinksComponent disabled={false}>
						<ContainerActions>
							<SimpleButton
								onClick={() => handleDownloadVoucher(rowData)}
								disabled={!rowData.urlCEP && !rowData.validationMailPdf}
							>
								{flowBanksOrStp && (rowData.urlCEP || rowData.validationMailPdf) ? (
									<TicketIcon />
								) : (
									<SkeletonSVG />
								)}
							</SimpleButton>
							<SimpleButton onClick={() => handleShowPaymentModal(rowData)}>
								<EyeIcon />
							</SimpleButton>
						</ContainerActions>
					</ContainerActionsLinksComponent>
				),
			},
		];

		return columns;
	};

	useEffect(() => {
		const trackInfo: PayloadProps = {
			type: 'screen',
			pageView: ANALYTICS.payment_links.page_view,
			source: 'GTM',
		};
		track(trackInfo);
	}, []);

	return (
		<ViewWrapper
			title="Links de Pago"
			isLoading={isLoadingGraph || isLoadingTable || isFetching || isFetcingStatistics}
		>
			<>
				{modal.show && modal.type === 'single' && validatePermission('PL_NEW') && (
					<NewLinkPL show={modal.show} onClose={onClose} />
				)}
				{modalPayment.show && (
					<ModalPayment
						show={modalPayment.show}
						onClose={onClosePaymentModal}
						data={modalPayment.data}
						positionType="top-right"
					/>
				)}

				<HeadContainer>
					<TitlePage>
						<PaymentLinksIcon /> Links de Pago
					</TitlePage>

					<ButtonsContainer>
						<TimeZoneContainer>
							{width > 576 && <TimeZone>Horario Corriente</TimeZone>}

							<DatePlPicker date={currentDate} setDate={setCurrentDate} />
						</TimeZoneContainer>
						<NewPaymentLinkButton onClick={() => handleShowModal('single')}>
							Crear nuevo link
						</NewPaymentLinkButton>
					</ButtonsContainer>
				</HeadContainer>

				<ResumeContainer>
					<ResumeSection data={statistics} />
				</ResumeContainer>

				<TableFilterHeader>
					<DateFilter
						filters={filters}
						onFilterChange={setFilters}
						pagination={pagination}
						setPagination={setPagination}
					/>
				</TableFilterHeader>

				{width > 576 && <PaymentLinkCustomHeader data={graphs} loading={isLoadingGraph} />}

				<PaymentLinkTable
					titleTable="Últimos pagos recibidos"
					allowedCustomAction={true}
					data={table?.tableInfo || []}
					columns={getColumns()}
					isLoading={isFetching && !isFetched}
					stateList={table?.typeOfStatus || []}
					filters={filters}
					onFilterChange={setFilters}
				/>
			</>
		</ViewWrapper>
	);
};

export { PaymentLinks3 };
